import { Link } from "react-router-dom";

export default function StripWaxCarpet() {
  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded flex items-center justify-center">
              <i className="ri-building-4-line text-xl text-[#080808]"></i>
            </div>
            <span className="font-['Barlow_Condensed'] text-2xl font-bold tracking-wide">SURE KLEEN</span>
          </Link>
          <Link to="/" className="text-sm font-['Space_Grotesk'] text-white/70 hover:text-[#00E5FF] transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line mr-2"></i>Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/013e337c95df0a27b4b56fc87ae87dd0.jpg"
            alt="Strip Wax and Carpet Care"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#080808]"></div>
        </div>
        <div className="relative z-10 text-center max-w-4xl px-6">
          <div className="inline-block px-4 py-2 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full mb-6">
            <span className="font-['Space_Mono'] text-sm text-[#00E5FF] tracking-wider">NAICS 561740</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-6xl md:text-7xl font-bold mb-6 tracking-tight">
            Strip, Wax & Carpet Care
          </h1>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Restoring and protecting your floors with precision refinishing and deep extraction cleaning
          </p>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            What We <span className="text-[#00E5FF]">Deliver</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "ri-contrast-drop-2-line",
                title: "Complete Strip & Refinish",
                desc: "Total removal of old wax buildup, deep scrubbing, and multi-coat application for showroom shine"
              },
              {
                icon: "ri-paint-brush-line",
                title: "High-Speed Burnishing",
                desc: "Diamond-pad polishing for ultra-high gloss finish on VCT and terrazzo surfaces"
              },
              {
                icon: "ri-drop-line",
                title: "Hot Water Extraction",
                desc: "Truck-mounted carpet cleaning systems for deep soil removal and rapid drying"
              },
              {
                icon: "ri-shield-line",
                title: "Stain & Odor Treatment",
                desc: "Enzyme-based solutions for pet stains, food spills, and embedded odors"
              },
              {
                icon: "ri-recycle-line",
                title: "Eco-Friendly Products",
                desc: "Low-VOC finishes and Green Seal certified cleaning agents"
              },
              {
                icon: "ri-calendar-check-line",
                title: "Scheduled Maintenance",
                desc: "Quarterly or annual programs to extend floor life and maintain appearance"
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-8 hover:border-[#00E5FF]/50 transition-all group">
                <div className="w-14 h-14 bg-[#00E5FF]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <i className={`${item.icon} text-3xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-8">
            Why It <span className="text-[#00E5FF]">Matters</span>
          </h2>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed mb-6">
            Floors are the largest visible surface in any facility—and the first to show wear. Proper maintenance isn't just about appearance; it's about protecting your investment, extending material lifespan, and maintaining a professional environment.
          </p>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed">
            Sure Kleen's floor care specialists use commercial-grade equipment and proven techniques to restore, protect, and maintain hard surfaces and carpets to institutional standards.
          </p>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Our <span className="text-[#00E5FF]">Process</span>
          </h2>
          <div className="space-y-8">
            {[
              {
                step: "01",
                title: "Floor Assessment",
                desc: "Identification of surface type, condition, traffic patterns, and maintenance history"
              },
              {
                step: "02",
                title: "Pre-Treatment",
                desc: "Application of stripping solution or carpet pre-spray to break down soils and old finish"
              },
              {
                step: "03",
                title: "Deep Cleaning",
                desc: "Mechanical scrubbing or hot water extraction to remove embedded dirt and residue"
              },
              {
                step: "04",
                title: "Refinishing or Grooming",
                desc: "Multi-coat wax application with cure time, or carpet grooming for uniform appearance"
              },
              {
                step: "05",
                title: "Final Inspection",
                desc: "Quality check for uniform coverage, proper drying, and client walkthrough"
              }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-8 items-start group">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#080808]">{item.step}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="font-['Space_Grotesk'] text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/70 leading-relaxed text-lg">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards & Compliance */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Standards & <span className="text-[#00E5FF]">Compliance</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-leaf-line",
                title: "Green Seal Certified",
                desc: "Environmentally responsible products that meet third-party sustainability standards"
              },
              {
                icon: "ri-tools-line",
                title: "IICRC Trained",
                desc: "Institute of Inspection, Cleaning and Restoration Certification for carpet care"
              },
              {
                icon: "ri-shield-check-line",
                title: "Bonded & Insured",
                desc: "Full liability coverage for high-value flooring and facility protection"
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 bg-[#00E5FF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-4xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-6">
            Restore Your Floors to Showroom Condition
          </h2>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 mb-10">
            Let Sure Kleen bring professional-grade floor care to your facility.
          </p>
          <a
            href="/#contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-[#080808] font-['Space_Grotesk'] font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all whitespace-nowrap"
          >
            Request Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-['Space_Mono'] text-sm text-white/50">
            © 2026 Sure Kleen Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}